import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum } from "@finance/lib/api";
import { toast } from "sonner";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Switch } from "@finance/components/ui/switch";
import { CurrencySelect } from "@finance/components/CurrencySelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { Plus, PencilSimple, Trash, ArrowsClockwise } from "@phosphor-icons/react";

const TYPES = ["Asset", "Liability", "Equity", "Income", "Expense"];
const SECTIONS = ["Revenue", "COGS", "Operating Expenses", "Balance Sheet"];

export default function CoaManager() {
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [valuation, setValuation] = useState(null);

  const load = useCallback(() => {
    api.get("/gl/mapping").then((r) => setAccounts(r.data));
    api.get("/gl/groups").then((r) => setGroups(r.data));
    api.get("/gl/cost-centres").then((r) => setCcs(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Tabs defaultValue="accounts">
      <TabsList data-testid="coa-subtabs">
        <TabsTrigger value="accounts" data-testid="subtab-accounts">Accounts</TabsTrigger>
        <TabsTrigger value="groups" data-testid="subtab-groups">Groups</TabsTrigger>
        <TabsTrigger value="cc" data-testid="subtab-cc">Cost Centres</TabsTrigger>
        <TabsTrigger value="mapping" data-testid="subtab-mapping">Mapping</TabsTrigger>
        <TabsTrigger value="inventory" data-testid="subtab-inventory">Inventory</TabsTrigger>
      </TabsList>

      <TabsContent value="accounts" className="mt-5"><Accounts accounts={accounts} groups={groups} ccs={ccs} onChange={load} /></TabsContent>
      <TabsContent value="groups" className="mt-5"><Groups groups={groups} onChange={load} /></TabsContent>
      <TabsContent value="cc" className="mt-5"><CostCentres ccs={ccs} onChange={load} /></TabsContent>
      <TabsContent value="mapping" className="mt-5"><Mapping accounts={accounts} groups={groups} ccs={ccs} onChange={load} /></TabsContent>
      <TabsContent value="inventory" className="mt-5"><InventoryTab valuation={valuation} setValuation={setValuation} onRebuild={load} /></TabsContent>
    </Tabs>
  );
}

// ---------------- Accounts ----------------
function Accounts({ accounts, groups, ccs, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const isEdit = f && f._edit;
  const start = (a) => { setF(a ? { ...a, _edit: true } : { code: "", name: "", type: "Expense", active: true, group_id: null, default_cost_centre_id: null, currency: "TZS", is_cash: false }); setOpen(true); };
  const save = async () => {
    if (!f.code || !f.name) { toast.error("Code and name are required."); return; }
    try {
      const body = { code: f.code, name: f.name, type: f.type, active: f.active, group_id: f.group_id || null, default_cost_centre_id: f.default_cost_centre_id || null, currency: f.currency || "TZS", is_cash: !!f.is_cash };
      if (isEdit) await api.put(`/gl/accounts/${f.code}`, body); else await api.post("/gl/accounts", body);
      toast.success("Saved"); setOpen(false); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (a) => { try { await api.patch(`/gl/accounts/${a.code}/toggle`); onChange(); } catch (e) { toast.error(e.response?.data?.detail); } };
  const del = async (a) => { if (!window.confirm(`Delete account ${a.code}?`)) return; try { await api.delete(`/gl/accounts/${a.code}`); toast.success("Deleted"); onChange(); } catch (e) { toast.error(e.response?.data?.detail); } };

  return (
    <div>
      <div className="mb-3 flex justify-end"><Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="add-account" onClick={() => start(null)}><Plus size={15} className="mr-1" />New Account</Button></div>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
            <th className="px-4 py-2 text-left">Code</th><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Group</th><th className="px-4 py-2 text-left">Currency</th><th className="px-4 py-2 text-center">Active</th><th className="px-4 py-2 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.code} data-testid={`acct-row-${a.code}`} className={`border-b border-gray-100 ${a.active ? "" : "opacity-50"}`}>
                <td className="px-4 py-2 font-mono-num">{a.code}</td><td className="px-4 py-2">{a.name}</td>
                <td className="px-4 py-2 text-gray-500">{a.type}</td><td className="px-4 py-2 text-gray-500">{a.group_name || "—"}</td>
                <td className="px-4 py-2 text-gray-500" data-testid={`acct-currency-${a.code}`}>{a.currency || "TZS"}{a.is_cash ? " · Cash" : ""}</td>
                <td className="px-4 py-2 text-center"><Switch checked={!!a.active} data-testid={`acct-active-${a.code}`} onCheckedChange={() => toggle(a)} /></td>
                <td className="px-4 py-2 text-right">
                  <button className="mr-3 text-gray-400 hover:text-[#1B4383]" data-testid={`acct-edit-${a.code}`} onClick={() => start(a)}><PencilSimple size={16} /></button>
                  <button className="text-gray-400 hover:text-red-500" data-testid={`acct-del-${a.code}`} onClick={() => del(a)}><Trash size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit" : "New"} Account</DialogTitle>
            <DialogDescription>GL account — all logins can create, edit and deactivate.</DialogDescription></DialogHeader>
          {f && (
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="lx-label">Code</Label><Input className="mt-1" data-testid="acct-code" value={f.code} disabled={isEdit} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
              <div><Label className="lx-label">Type</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className="mt-1" data-testid="acct-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2"><Label className="lx-label">Name</Label><Input className="mt-1" data-testid="acct-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div><Label className="lx-label">Group</Label>
                <Select value={f.group_id || "none"} onValueChange={(v) => setF({ ...f, group_id: v === "none" ? null : v })}><SelectTrigger className="mt-1" data-testid="acct-group"><SelectValue placeholder="Group" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.section} · {g.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="lx-label">Default Cost Centre</Label>
                <Select value={f.default_cost_centre_id || "none"} onValueChange={(v) => setF({ ...f, default_cost_centre_id: v === "none" ? null : v })}><SelectTrigger className="mt-1" data-testid="acct-cc"><SelectValue placeholder="Cost centre" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{ccs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="lx-label">Currency</Label>
                <CurrencySelect value={f.currency || "TZS"} onValueChange={(v) => setF({ ...f, currency: v })} testid="acct-currency" className="mt-1" allowCreate={false} /></div>
              <label className="flex items-end gap-2 pb-2 text-sm"><Switch data-testid="acct-is-cash" checked={!!f.is_cash} onCheckedChange={(v) => setF({ ...f, is_cash: v })} /> Is cash account</label>
              <label className="col-span-2 flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Active</label>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-account" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Groups ----------------
function Groups({ groups, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const isEdit = f && f.id;
  const start = (g) => { setF(g ? { ...g } : { name: "", section: "Operating Expenses", order: 1 }); setOpen(true); };
  const save = async () => {
    if (!f.name) { toast.error("Name required."); return; }
    try {
      const body = { name: f.name, section: f.section, order: Number(f.order) || 1 };
      if (isEdit) await api.put(`/gl/groups/${f.id}`, body); else await api.post("/gl/groups", body);
      toast.success("Saved"); setOpen(false); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (g) => { if (!window.confirm(`Delete group ${g.name}?`)) return; try { await api.delete(`/gl/groups/${g.id}`); onChange(); } catch (e) { toast.error(e.response?.data?.detail); } };
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">P&amp;L roll-up order: Revenue → COGS → Gross Profit → Operating Expenses → Net Profit.</p>
        <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="add-group" onClick={() => start(null)}><Plus size={15} className="mr-1" />New Group</Button>
      </div>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm"><thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
          <th className="px-4 py-2 text-left">Section</th><th className="px-4 py-2 text-left">Group</th><th className="px-4 py-2 text-right">Order</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
          <tbody>{groups.map((g) => (
            <tr key={g.id} data-testid={`group-row-${g.id}`} className="border-b border-gray-100">
              <td className="px-4 py-2 text-gray-500">{g.section}</td><td className="px-4 py-2">{g.name}</td><td className="px-4 py-2 text-right lx-num">{g.order}</td>
              <td className="px-4 py-2 text-right">
                <button className="mr-3 text-gray-400 hover:text-[#1B4383]" data-testid={`group-edit-${g.id}`} onClick={() => start(g)}><PencilSimple size={16} /></button>
                <button className="text-gray-400 hover:text-red-500" data-testid={`group-del-${g.id}`} onClick={() => del(g)}><Trash size={15} /></button>
              </td></tr>))}</tbody></table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent data-testid="group-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit" : "New"} Group</DialogTitle>
          <DialogDescription>Groups bucket accounts for the P&amp;L subtotals.</DialogDescription></DialogHeader>
        {f && (<div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label className="lx-label">Name</Label><Input className="mt-1" data-testid="group-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label className="lx-label">Section</Label><Select value={f.section} onValueChange={(v) => setF({ ...f, section: v })}><SelectTrigger className="mt-1" data-testid="group-section"><SelectValue /></SelectTrigger>
            <SelectContent>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="lx-label">Order</Label><Input type="number" className="mt-1" data-testid="group-order" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} /></div>
        </div>)}
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-group" onClick={save}>Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ---------------- Cost Centres ----------------
function CostCentres({ ccs, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const isEdit = f && f.id;
  const start = (c) => { setF(c ? { ...c } : { name: "", code: "", active: true }); setOpen(true); };
  const save = async () => {
    if (!f.name) { toast.error("Name required."); return; }
    try {
      const body = { name: f.name, code: f.code || null, active: f.active };
      if (isEdit) await api.put(`/gl/cost-centres/${f.id}`, body); else await api.post("/gl/cost-centres", body);
      toast.success("Saved"); setOpen(false); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => { if (!window.confirm(`Delete cost centre ${c.name}?`)) return; try { await api.delete(`/gl/cost-centres/${c.id}`); onChange(); } catch (e) { toast.error(e.response?.data?.detail); } };
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">Tag branches, departments or projects on transactions, expenses and journals for later report slicing.</p>
        <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="add-cc" onClick={() => start(null)}><Plus size={15} className="mr-1" />New Cost Centre</Button>
      </div>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm"><thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
          <th className="px-4 py-2 text-left">Code</th><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-center">Active</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
          <tbody>{ccs.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No cost centres yet.</td></tr>}
            {ccs.map((c) => (<tr key={c.id} data-testid={`cc-row-${c.id}`} className={`border-b border-gray-100 ${c.active ? "" : "opacity-50"}`}>
              <td className="px-4 py-2 font-mono-num">{c.code || "—"}</td><td className="px-4 py-2">{c.name}</td>
              <td className="px-4 py-2 text-center">{c.active ? "Yes" : "No"}</td>
              <td className="px-4 py-2 text-right">
                <button className="mr-3 text-gray-400 hover:text-[#1B4383]" data-testid={`cc-edit-${c.id}`} onClick={() => start(c)}><PencilSimple size={16} /></button>
                <button className="text-gray-400 hover:text-red-500" data-testid={`cc-del-${c.id}`} onClick={() => del(c)}><Trash size={15} /></button>
              </td></tr>))}</tbody></table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent data-testid="cc-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit" : "New"} Cost Centre</DialogTitle>
          <DialogDescription>A custom slice dimension, independent of account type.</DialogDescription></DialogHeader>
        {f && (<div className="grid grid-cols-2 gap-4">
          <div><Label className="lx-label">Code</Label><Input className="mt-1" data-testid="cc-code" value={f.code || ""} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
          <div><Label className="lx-label">Name</Label><Input className="mt-1" data-testid="cc-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Active</label>
        </div>)}
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-cc" onClick={save}>Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ---------------- Mapping ----------------
function Mapping({ accounts, groups, ccs, onChange }) {
  const saveField = async (a, patch) => {
    try {
      await api.put(`/gl/accounts/${a.code}`, {
        code: a.code, name: a.name, type: a.type, active: a.active,
        group_id: a.group_id || null, default_cost_centre_id: a.default_cost_centre_id || null, ...patch,
      });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
          <th className="px-4 py-2 text-left">Code</th><th className="px-4 py-2 text-left">Account</th><th className="px-4 py-2 text-left">Type</th>
          <th className="px-4 py-2 text-left">Group</th><th className="px-4 py-2 text-left">Default Cost Centre</th></tr></thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.code} data-testid={`map-row-${a.code}`} className={`border-b border-gray-100 ${a.active ? "" : "opacity-50"}`}>
              <td className="px-4 py-2 font-mono-num">{a.code}</td><td className="px-4 py-2">{a.name}</td><td className="px-4 py-2 text-gray-500">{a.type}</td>
              <td className="px-4 py-2">
                <Select value={a.group_id || "none"} onValueChange={(v) => saveField(a, { group_id: v === "none" ? null : v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`map-group-${a.code}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.section} · {g.name}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="px-4 py-2">
                <Select value={a.default_cost_centre_id || "none"} onValueChange={(v) => saveField(a, { default_cost_centre_id: v === "none" ? null : v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`map-cc-${a.code}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{ccs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Inventory (perpetual AVCO) ----------------
function InventoryTab({ valuation, setValuation, onRebuild }) {
  const load = useCallback(() => { api.get("/gl/inventory/valuation").then((r) => setValuation(r.data)); }, [setValuation]);
  useEffect(() => { load(); }, [load]);
  const rebuild = async () => {
    try { const r = await api.post("/gl/inventory/rebuild"); toast.success(`Rebuilt — ${r.data.journals_posted} journals, closing ${fmtNum(r.data.closing_inventory_value)}`); load(); onRebuild(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">Perpetual weighted-average (AVCO). Receipts (GRV) debit Inventory at landed cost; issues (Delivery Notes) credit Inventory & debit COGS at the running average.</p>
        <Button size="sm" variant="outline" data-testid="rebuild-inventory" onClick={rebuild}><ArrowsClockwise size={15} className="mr-1" />Rebuild Inventory GL</Button>
      </div>
      {valuation && (
        <>
          <div className="mb-3 rounded-sm border border-gray-200 bg-white p-4">
            <span className="lx-label">Closing Inventory Value (GL account 1200)</span>
            <div className="mt-1 font-display text-2xl font-bold lx-num text-[#122E5C]" data-testid="closing-inventory">TSh {fmtNum(valuation.closing_inventory_value)}</div>
          </div>
          <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
            <table className="w-full text-sm"><thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Item</th><th className="px-4 py-2 text-right">Qty on hand</th><th className="px-4 py-2 text-right">Avg cost</th><th className="px-4 py-2 text-right">Value</th></tr></thead>
              <tbody>{valuation.valuation.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No stock on hand.</td></tr>}
                {valuation.valuation.map((v) => (<tr key={v.key} data-testid={`val-row-${v.key}`} className="border-b border-gray-100">
                  <td className="px-4 py-2">{v.key}</td><td className="px-4 py-2 text-right lx-num">{fmtNum(v.qty)}</td>
                  <td className="px-4 py-2 text-right lx-num">{fmtNum(v.avg_cost)}</td><td className="px-4 py-2 text-right lx-num">{fmtNum(v.value)}</td></tr>))}</tbody></table>
          </div>
        </>
      )}
    </div>
  );
}
